import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { local } from '@/api/api';
import { MenuType } from '@/types';
import { PromotionBannerData } from '@/types/promotion-banner';

export type ScarcityBannerSettings = {
  title: string;
  description: string;
  activation_time_minutes: number;
  countdown_start_minutes: number;
  vehicle_image: { url: string } | null;
  secondary_banner_text: string;
  main_banner_background_color: string;
  main_banner_text_color: string;
  secondary_banner_background_color: string;
  secondary_banner_text_color: string;
  decorative_circles_color: string;
  countdown_background_color: string;
  countdown_digit_color: string;
  countdown_label_color: string;
  countdown_separator_color: string;
  countdown_border_color: string;
};

export type LayoutType = {
  seo: {
    title: string;
    meta_tags: any[];
  };
  breadcrumbs: BreadCrumbType[];
  menus: MenuType[];
};

export type BreadCrumbType = {
  label: string;
  href: string;
  active: boolean;
};

export type ReservationNavModeType = "default" | "hide" | "floater";

export type LayoutProviderContext = {
  layout: LayoutType;
  setLayout: Dispatch<SetStateAction<LayoutType>>;
  setData: (d: LayoutType) => void;
  breadcrumbs: any;
  setMenu: (v: MenuType[]) => void;
  isPaymentResumeOpen: boolean;
  setIsPaymentResumeOpen: (payload: boolean) => void;
  reservationNavMode: ReservationNavModeType;
  setReservationNavMode: (mode: ReservationNavModeType) => void;
  downloadApp: boolean;
  setDownloadApp: (downloadApp: boolean) => void;
  isScarcityBannerVisible: boolean;
  setIsScarcityBannerVisible: (visible: boolean) => void;
  isPromotionBannerVisible: boolean;
  setIsPromotionBannerVisible: (visible: boolean) => void;
  couponToApply: string | null;
  setCouponToApply: (coupon: string | null) => void;
  promotionBannerData: PromotionBannerData | null;
  paymentResumeHeight: number;
  setPaymentResumeHeight: (height: number) => void;
  scarcityBannerSettings: ScarcityBannerSettings | null;
};

export type LayoutProviderProps = PropsWithChildren & {};

const defaults: LayoutType = {
  seo: {
    title: 'Rent a Car - Alugue Foco',
    meta_tags: [],
  },
  breadcrumbs: [],
  menus: [],
};

const Context = createContext<LayoutProviderContext>(
  {} as LayoutProviderContext,
);

export function LayoutProvider({ children }: LayoutProviderProps) {
  const [layout, setLayout] = useState<LayoutType>(defaults);
  const [isPaymentResumeOpen, setIsPaymentResumeOpen] = useState(false);
  const [reservationNavMode, setReservationNavMode] = useState<ReservationNavModeType>('default');
  const [downloadApp, setDownloadApp] = useState(false);
  const [isScarcityBannerVisible, setIsScarcityBannerVisible] = useState(false);
  const [isPromotionBannerVisible, setIsPromotionBannerVisible] = useState(false);
  const [couponToApply, setCouponToApply] = useState<string | null>(null);
  const [promotionBannerData, setPromotionBannerData] = useState<PromotionBannerData | null>(null);

  useEffect(() => {
    async function fetchPromotionBannerData() {
      try {
        const { data } = await local('/api/promotion-banner');
        setPromotionBannerData(data);
      } catch (err) {
        console.error('Failed to fetch promotion banner data:', err);
      }
    }
    fetchPromotionBannerData();
  }, []);
  const [paymentResumeHeight, setPaymentResumeHeight] = useState<number>(0);
  const [scarcityBannerSettings, setScarcityBannerSettings] = useState<ScarcityBannerSettings | null>(null);

  useEffect(() => {
    async function fetchScarcitySettings() {
      try {
        const { data } = await local('/api/scarcity-banner');
        setScarcityBannerSettings(data);
      } catch (err) {
        console.error('Failed to fetch scarcity banner settings:', err);
      }
    }
    fetchScarcitySettings();
  }, []);

  const setData = (data: Partial<LayoutType>) => {
    setLayout({
      ...layout,
      ...data,
    });
  };

  const setMenu = (menus: MenuType[]) => {
    setLayout({
      ...layout,
      menus,
    });
  };

  const breadcrumbs = {
    push(data: BreadCrumbType) {
      setData({
        breadcrumbs: [...layout.breadcrumbs, data],
      });
    },
    reset() {
      setData({
        breadcrumbs: defaults.breadcrumbs,
      });
    },
    set(data: BreadCrumbType[]) {
      setData({
        breadcrumbs: data,
      });
    },
  };

  return (
    <Context.Provider
      value={{ layout, setLayout, setData, breadcrumbs, setMenu, isPaymentResumeOpen, setIsPaymentResumeOpen, reservationNavMode, setReservationNavMode, downloadApp, setDownloadApp, isScarcityBannerVisible, setIsScarcityBannerVisible, paymentResumeHeight, setPaymentResumeHeight, scarcityBannerSettings, isPromotionBannerVisible, setIsPromotionBannerVisible, couponToApply, setCouponToApply, promotionBannerData }}
    >
      {children}
    </Context.Provider>
  );
}

export function useLayout() {
  return useContext(Context);
}
